import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Send } from 'lucide-react'

import {
  fetchCommunityPostById,
  fetchCommunityVoteDirectionsForPostsAndUser,
  netVoteScoreForPost,
  setCommunityPostVote,
  type CommunityPost,
  type CommunityVoteDirection,
} from '@/lib/communityPostData'
import {
  COMMUNITY_COMMENT_MAX_LENGTH,
  createCommunityComment,
} from '@/lib/communityComments'
import { formatShortTimeAgo } from '@/lib/communityTimeBuckets'
import {
  boardSubredditTitle,
  isArtistOnlyBoard,
  resolveCommunityPostPlaceLabel,
} from '@/lib/communityBoards'

import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AuthModal } from '@/components/auth/AuthModal'
import { Toast, useToast } from '@/components/ui/toast'
import { CommunityCommentThread } from '@/components/community/CommunityCommentThread'
import { CommunityPostDetailAuthorRow } from '@/components/community/CommunityPostDetailAuthorRow'
import { CommunityPostCarousel } from '@/components/community/CommunityPostCarousel'
import { CommunityVoteBar } from '@/components/community/CommunityVoteBar'

import { auth } from '@/lib/firebase'

function buildAuthorDisplayName(params: {
  displayName?: string | null
  instagram?: string | null
  email?: string | null
}): string | null {
  if (params.displayName?.trim().length) return params.displayName.trim()
  if (params.instagram?.trim().length) return `@${params.instagram.trim()}`
  if (params.email?.trim().length) return params.email.trim()
  return null
}

interface CommunityPostDetailContentProps {
  readonly postId: string
  readonly onBack: () => void
  readonly onPostUpdated?: (post: CommunityPost) => void
  readonly onViewerVoteDirectionChange?: (
    postId: string,
    direction: CommunityVoteDirection | null,
  ) => void
}

export function CommunityPostDetailContent({
  postId,
  onBack,
  onPostUpdated,
  onViewerVoteDirectionChange,
}: CommunityPostDetailContentProps) {
  const { t } = useTranslation()
  const { user: authUser } = useAuthState()
  const { userData, isArtist } = useUserData()
  const { toast, showToast, hideToast } = useToast()

  const [post, setPost] = useState<CommunityPost | null>(null)
  const [postLoading, setPostLoading] = useState(true)
  const [postNotFound, setPostNotFound] = useState(false)

  const [commentDraftBody, setCommentDraftBody] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [commentThreadKey, setCommentThreadKey] = useState(0)

  const [viewerVoteDirection, setViewerVoteDirection] = useState<CommunityVoteDirection | null>(
    null,
  )
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const pendingVoteIntentRef = useRef<CommunityVoteDirection | null>(null)
  const pendingCommentDraftRef = useRef<string | null>(null)
  const commentsContainerRef = useRef<HTMLDivElement | null>(null)

  const viewerUserId = authUser?.uid ?? null
  const viewerAuthorDisplayName = buildAuthorDisplayName({
    displayName: userData?.displayName,
    instagram: userData?.instagram,
    email: authUser?.email,
  })

  const notifyPostUpdated = useCallback(
    (updatedPost: CommunityPost) => {
      onPostUpdated?.(updatedPost)
    },
    [onPostUpdated],
  )

  const refreshPost = useCallback(async () => {
    try {
      const fetched = await fetchCommunityPostById(postId)
      if (fetched == null) {
        setPostNotFound(true)
        setPost(null)
      } else {
        setPost(fetched)
        notifyPostUpdated(fetched)
      }
    } catch {
      showToast(String(t('community.loadFailed')))
    } finally {
      setPostLoading(false)
    }
  }, [notifyPostUpdated, postId, showToast, t])

  useEffect(() => {
    setPostLoading(true)
    setPostNotFound(false)
    setPost(null)
    setCommentDraftBody('')
    void refreshPost()
  }, [postId, refreshPost])

  useEffect(() => {
    if (!viewerUserId) {
      setViewerVoteDirection(null)
      return
    }
    let cancelled = false
    void fetchCommunityVoteDirectionsForPostsAndUser([postId], viewerUserId).then((map) => {
      if (cancelled) return
      setViewerVoteDirection(map.get(postId) ?? null)
    })
    return () => {
      cancelled = true
    }
  }, [postId, viewerUserId])

  const handlePressVote = useCallback(
    async (direction: CommunityVoteDirection) => {
      if (!post) return
      const currentUserId = auth.currentUser?.uid
      if (!currentUserId) {
        pendingVoteIntentRef.current = direction
        setAuthModalOpen(true)
        return
      }
      try {
        const { resultingDirection } = await setCommunityPostVote({
          postId,
          userId: currentUserId,
          pressedDirection: direction,
        })
        setViewerVoteDirection(resultingDirection)
        onViewerVoteDirectionChange?.(postId, resultingDirection)
        await refreshPost()
      } catch {
        showToast(String(t('community.voteFailed')))
      }
    },
    [onViewerVoteDirectionChange, post, postId, refreshPost, showToast, t],
  )

  const handleSubmitComment = useCallback(async () => {
    if (!post) return
    const trimmedBody = commentDraftBody.trim()
    if (trimmedBody.length === 0) return

    const currentUserId = auth.currentUser?.uid
    if (!currentUserId) {
      pendingCommentDraftRef.current = trimmedBody
      setAuthModalOpen(true)
      return
    }
    if (isArtistOnlyBoard(post.boardId) && !isArtist) {
      showToast(String(t('community.artistsDeskOnlyCommenting')))
      return
    }

    setSubmittingComment(true)
    try {
      await createCommunityComment({
        postId,
        userId: currentUserId,
        authorDisplayName: viewerAuthorDisplayName,
        body: trimmedBody,
      })
      setCommentDraftBody('')
      setCommentThreadKey((current) => current + 1)
      await refreshPost()
      requestAnimationFrame(() => {
        commentsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      })
    } catch {
      showToast(String(t('community.commentSendFailed')))
    } finally {
      setSubmittingComment(false)
    }
  }, [
    commentDraftBody,
    isArtist,
    post,
    postId,
    refreshPost,
    showToast,
    t,
    viewerAuthorDisplayName,
  ])

  const replayAfterLogin = useCallback(() => {
    const pendingVote = pendingVoteIntentRef.current
    const pendingComment = pendingCommentDraftRef.current
    pendingVoteIntentRef.current = null
    pendingCommentDraftRef.current = null
    if (pendingVote) {
      window.setTimeout(() => void handlePressVote(pendingVote), 300)
    } else if (pendingComment) {
      setCommentDraftBody(pendingComment)
      window.setTimeout(() => void handleSubmitComment(), 300)
    }
  }, [handlePressVote, handleSubmitComment])

  const draftCharsLeft = COMMUNITY_COMMENT_MAX_LENGTH - commentDraftBody.length

  if (postNotFound) {
    return (
      <div className="bg-background">
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion">
          <div className="flex items-center gap-1.5 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
              aria-label={String(t('community.detail.backAria'))}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold">{t('common.error')}</h1>
          </div>
          <div className="text-center py-16">
            <p className="text-muted-foreground">{t('community.detail.postNotFound')}</p>
            <Button onClick={onBack} className="mt-4">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-background pb-32">
      <Toast message={toast.message} isVisible={toast.isVisible} onClose={hideToast} />

      <div className="phone-container-padded pb-4 pt-safe-native-top-cushion">
        <div className="space-y-3">
          <header className="space-y-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                aria-label={String(t('community.detail.backAria'))}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              {postLoading || !post ? (
                <div className="h-3.5 min-w-0 flex-1 animate-pulse rounded bg-muted/50" />
              ) : (
                <p className="flex h-8 min-w-0 flex-1 items-center truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {boardSubredditTitle(post.boardId)}
                </p>
              )}
            </div>
            {postLoading || !post ? (
              <div className="space-y-2 pt-1">
                <div className="h-7 w-full animate-pulse rounded bg-muted/50" />
                <div className="h-7 w-4/5 animate-pulse rounded bg-muted/50" />
              </div>
            ) : (
              <h1 className="text-2xl font-semibold leading-snug text-foreground">{post.title}</h1>
            )}
          </header>

          {postLoading || !post ? (
            <div className="space-y-4">
              <div className="h-48 animate-pulse rounded-2xl bg-muted/50" />
              <div className="h-16 animate-pulse rounded bg-muted/50" />
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 shrink-0 animate-pulse rounded-2xl bg-muted/50" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-muted/50" />
                  <div className="h-3 w-1/4 animate-pulse rounded bg-muted/50" />
                </div>
              </div>
            </div>
          ) : (
            <article className="space-y-3">
              {post.imageUrls.length > 0 && <CommunityPostCarousel imageUrls={post.imageUrls} />}

              {(post.body?.trim() ?? '').length > 0 && (
                <p className="whitespace-pre-wrap text-sm leading-[1.4] text-foreground/90">
                  {post.body}
                </p>
              )}

              <CommunityPostDetailAuthorRow
                post={post}
                placeLabel={resolveCommunityPostPlaceLabel({
                  boardId: post.boardId,
                  cityDisplayLabel: post.cityDisplayLabel,
                  worldwideLabel: String(t('community.worldwide')),
                })}
                ageLabel={formatShortTimeAgo(post.createdAt)}
              />

              <CommunityVoteBar
                viewerDirection={viewerVoteDirection}
                netScore={netVoteScoreForPost(post)}
                commentCount={post.commentCount}
                onPressUpvote={() => void handlePressVote('up')}
                onPressDownvote={() => void handlePressVote('down')}
                onPressComments={() => {
                  commentsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              />
            </article>
          )}
        </div>

        <div ref={commentsContainerRef} className="mt-8 space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('community.detail.commentsHeader', { count: post?.commentCount ?? 0 })}
          </h2>

          <CommunityCommentThread
            key={commentThreadKey}
            postId={postId}
            viewerUserId={viewerUserId}
            authorDisplayNameForViewer={viewerAuthorDisplayName}
            onCommentCountChange={() => void refreshPost()}
          />
        </div>
      </div>

      <div
        className="fixed inset-x-0 bottom-0 z-[110] border-t border-border bg-background/95 backdrop-blur-sm"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="phone-container-padded py-2.5">
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              void handleSubmitComment()
            }}
          >
            <Textarea
              rows={1}
              maxLength={COMMUNITY_COMMENT_MAX_LENGTH}
              placeholder={String(t('community.detail.commentPlaceholder'))}
              value={commentDraftBody}
              onChange={(event) => setCommentDraftBody(event.target.value)}
              className="min-h-[42px] max-h-32 resize-none"
            />
            <Button
              type="submit"
              size="icon"
              disabled={submittingComment || commentDraftBody.trim().length === 0}
              aria-label={String(t('community.detail.sendCommentAria'))}
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          {commentDraftBody.length > COMMUNITY_COMMENT_MAX_LENGTH - 200 && (
            <p className="mt-1 text-right text-[10px] text-muted-foreground tabular-nums">
              {draftCharsLeft}
            </p>
          )}
        </div>
      </div>

      <AuthModal
        dismissible
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        onSuccess={replayAfterLogin}
        source="community-detail"
      />
    </div>
  )
}
